import type { Project, TimeLog, TimeLogCategory } from '@/lib/api';

/**
 * LP に出す工数明細の見本データ。
 *
 * **実データではない。** LP は未ログインの公開ページなので API を呼べず、
 * かといって作り物の図を描くと実物と食い違う。実物のコンポーネント
 * (`components/timelogs/TimeLogGrid`) に、ここで組み立てた固定の記録を流す。
 *
 * 名前はすべて一般名詞にする。人名は「メンバー A / B / C」で、実在の人物も
 * 組織も思わせない。数字も見本であることを節の文章と `SAMPLE` ラベルで明示する。
 *
 * 記録の入り方は MCP からの実際の入り方に寄せてある。1 日 8 時間ちょうどには
 * 揃わず (会話の区切りごとに入るため)、土日はほぼ空き、メモは AI が書いた
 * ものだけが付く。
 */

/** 対象月。月を変えると列数も変わる (`monthDays` が月の日数を決める)。 */
export const SAMPLE_MONTH = '2026-07';

export const SAMPLE_PROJECTS: Project[] = [
  {
    id: 'prj_sample_payment',
    name: '決済基盤',
    projectTypeId: 'pty_sample_product',
    projectTypeName: '自社プロダクト開発',
    isCapitalizable: true,
    status: 'active',
    budgetMode: 'monthly',
    mcpCustomInstruction: null,
    repositories: ['github.com/acme/payments-api'],
  },
  {
    id: 'prj_sample_mobile',
    name: 'モバイルアプリ刷新',
    projectTypeId: 'pty_sample_client',
    projectTypeName: '受託 / SES',
    isCapitalizable: false,
    status: 'active',
    budgetMode: 'total',
    mcpCustomInstruction: null,
    repositories: ['github.com/acme/mobile-app'],
  },
  {
    id: 'prj_sample_ops',
    name: '社内システム保守',
    projectTypeId: 'pty_sample_ops',
    projectTypeName: '社内業務・保守',
    isCapitalizable: false,
    status: 'active',
    budgetMode: 'none',
    mcpCustomInstruction: null,
    repositories: [],
  },
];

/** [日, 時間] か [日, 時間, メモ] */
type SampleDay = [number, number] | [number, number, string];

interface SampleRow {
  userId: string;
  userName: string;
  projectId: string;
  category: TimeLogCategory;
  days: SampleDay[];
}

const SAMPLE_ROWS: SampleRow[] = [
  {
    userId: 'usr_sample_a',
    userName: 'メンバー A',
    projectId: 'prj_sample_payment',
    category: 'development',
    days: [
      [1, 5.5],
      [2, 6],
      [3, 4.5, '返金フローの検証ロジックを実装。境界値のテストを追加した。'],
      [6, 6],
      [7, 5],
      [8, 6.5],
      [9, 4],
      [10, 5.5],
      [13, 6],
      [14, 6.5],
      [15, 5],
      [16, 4.5],
      [17, 6],
      [21, 5.5],
      [22, 6],
      [23, 4],
      [24, 5.5],
      [27, 6],
      [28, 5],
      [29, 6.5],
      [30, 4.5],
      [31, 5],
    ],
  },
  {
    userId: 'usr_sample_a',
    userName: 'メンバー A',
    projectId: 'prj_sample_ops',
    category: 'maintenance',
    days: [
      [2, 1],
      [9, 1.5],
      [16, 1],
      [23, 2],
      [30, 1],
    ],
  },
  {
    userId: 'usr_sample_b',
    userName: 'メンバー B',
    projectId: 'prj_sample_payment',
    category: 'development',
    days: [
      [1, 3],
      [2, 3.5],
      [6, 4],
      [7, 3],
      [8, 2.5],
      [13, 4],
      [14, 3.5],
      [15, 3],
      [20, 4],
      [21, 3],
      [22, 3.5],
      [27, 4],
      [28, 3],
      [29, 2.5],
    ],
  },
  {
    userId: 'usr_sample_b',
    userName: 'メンバー B',
    projectId: 'prj_sample_mobile',
    category: 'development',
    days: [
      [3, 4],
      [9, 4.5],
      [10, 5],
      [16, 4],
      [17, 4.5, '一覧の再取得まわりを実装。バックエンドの応答待ちで一旦中断。'],
      [23, 4],
      [24, 5],
      [30, 4.5],
      [31, 4],
    ],
  },
  {
    userId: 'usr_sample_c',
    userName: 'メンバー C',
    projectId: 'prj_sample_mobile',
    category: 'development',
    days: [
      [1, 6],
      [2, 5.5],
      [3, 6],
      [6, 5],
      [7, 6.5],
      [8, 6],
      [9, 5.5],
      [10, 6],
      [13, 5],
      [14, 6],
      [15, 6.5],
      [16, 5.5],
      [17, 6],
      [20, 5],
      [21, 6],
      [22, 5.5],
      [27, 6],
      [28, 5.5],
      [29, 6],
    ],
  },
  {
    userId: 'usr_sample_c',
    userName: 'メンバー C',
    projectId: 'prj_sample_ops',
    category: 'meeting',
    days: [
      [6, 1],
      [13, 1],
      [18, 2, '障害対応の待機から復旧確認まで。原因は監視設定の抜け。'],
      [20, 1],
      [27, 1],
    ],
  },
];

const PROJECT_BY_ID = new Map(SAMPLE_PROJECTS.map((project) => [project.id, project]));

/**
 * 資産化されるかはサーバーが導出する (`mcp/classify.ts` の `deriveIsCapitalized`)。
 * 見本でも同じ規則で決める——ここで自由に立てると、LP が製品と違う数字を出す。
 */
function derive(projectId: string, category: TimeLogCategory): boolean {
  return (PROJECT_BY_ID.get(projectId)?.isCapitalizable ?? false) && category === 'development';
}

export const SAMPLE_LOGS: TimeLog[] = SAMPLE_ROWS.flatMap((row, rowIndex) =>
  row.days.map(([day, hours, note]) => ({
    id: `sample-${rowIndex}-${day}`,
    userId: row.userId,
    userName: row.userName,
    projectId: row.projectId,
    projectName: PROJECT_BY_ID.get(row.projectId)?.name ?? row.projectId,
    hours,
    workedOn: `${SAMPLE_MONTH}-${String(day).padStart(2, '0')}`,
    category: row.category,
    isCapitalizedLog: derive(row.projectId, row.category),
    // 種別の既定 (自社プロダクト開発と社内業務は請求対象外)
    isBillable: row.projectId === 'prj_sample_mobile',
    note: note ?? null,
    source: 'mcp' as const,
    createdAt: 0,
    updatedAt: 0,
  })),
);
